import {CopyToClipboardIconButton} from '@github-ui/copy-to-clipboard/IconButton'
import {sendEvent} from '@github-ui/hydro-analytics'
import {DownloadIcon, LinkExternalIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {useState} from 'react'

import {useHydrateDiffFile} from '../../hooks/use-hydrate-diff-file'
import {useFileInfo} from '../../utils/content-preview-utils'
import type {FileDiff} from './content-preview-types'
import {useContentPreview} from './ContentPreviewContext'
import styles from './DiffPreview.module.css'
import {DiffView} from './DiffView'
import {DiffViewSettings} from './DiffViewSettings'
import {downloadFile} from './download-file'

interface DiffPreviewProps {
  fileDiff: FileDiff
}

export function DiffPreview({fileDiff}: DiffPreviewProps) {
  const {updateItem} = useContentPreview()

  const baseFile = fileDiff.baseFile
  const headFile = fileDiff.headFile
  const [isUnifiedDiff, setIsUnifiedDiff] = useState(true)

  const isDiffable = baseFile.value && headFile.value
  const isNewFile = !baseFile.value && headFile.value
  const isDeletedFile = baseFile.value && !headFile.value

  const {linesOfCode, size} = useFileInfo(isNewFile || isDiffable ? headFile : baseFile)
  const lineLabel = `${isNewFile ? 'Added: ' : isDeletedFile ? 'Deleted: ' : ''} ${linesOfCode} `

  useHydrateDiffFile(fileDiff, updateItem, 'base')
  useHydrateDiffFile(fileDiff, updateItem, 'head')

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <div className={styles.toolbarLeft}>
          <p className={styles.toolbarLoC}>
            {lineLabel} {linesOfCode === 1 ? 'line' : 'lines'} · {size}
          </p>
        </div>
        <div className={styles.toolbarActions}>
          {baseFile.reference && 'url' in baseFile.reference && (
            <IconButton
              as="a"
              aria-label="Open file in a new tab"
              icon={LinkExternalIcon}
              variant="invisible"
              href={baseFile.reference.url}
              target="_blank"
              rel="noopener noreferrer"
            />
          )}
          {/* Both copy and download return the head file */}
          <CopyToClipboardIconButton
            textToCopy={headFile.value}
            ariaLabel={'Copy code'}
            onCopy={() => sendEvent('dotcom_chat.activate', {target: 'BROWSER_FILE_COPY', mode: 'immersive'})}
          />
          <IconButton
            aria-label="Download code"
            icon={DownloadIcon}
            variant="invisible"
            onClick={() => {
              const fileName = headFile.name
              downloadFile(new File([headFile.value], fileName))
              sendEvent('dotcom_chat.activate', {target: 'BROWSER_FILE_DOWNLOAD', mode: 'immersive'})
            }}
          />
          {isDiffable && <DiffViewSettings setIsUnifiedDiff={setIsUnifiedDiff} isUnifiedDiff={isUnifiedDiff} />}
        </div>
      </div>
      <DiffView left={baseFile} right={headFile} view={isUnifiedDiff ? 'unified' : 'split'} />
    </div>
  )
}
